/*
 * Демо-набор для «Административных нарушений» — там, где у зеркала ЕРАП данных
 * нет вовсе.
 *
 * ЧЕГО НЕТ В ИСТОЧНИКЕ И ПОЧЕМУ. Разбивка по типам субъектов (ИП,
 * несовершеннолетние, должностные лица, иностранцы) существует в справочнике
 * `subjectType`, но реестр субъектов (физлиц) бэкенд не отдаёт. Наложенные
 * взыскания не хранятся ни в одном из доступных реестров ЕРАП: `torelik`
 * пуст, у протоколов полей о наказании нет.
 *
 * ДОЛИ ВЗЯТЫ ОТ НАСТОЯЩЕГО ИТОГА, А НЕ ВЫДУМАНЫ ЦЕЛИКОМ. На вход идёт реальное
 * число протоколов из `erap/protocols/summary`, и все строки — его проценты.
 * Поэтому набор не спорит с плиткой «Всего нарушений» над ним и меняется
 * вместе с ней, если зеркало дольют.
 *
 * ЭТО ЗАГЛУШКА, И ОНА ПОДПИСАНА НА ЭКРАНЕ. Числа правдоподобны, но не
 * измерены; блоки, которые их показывают, помечены «оценка».
 */

export type OrgGroupRow = {
  name: string
  total: number
  individuals: number
  legalEntities: number
  entrepreneurs: number
  minors: number
  officials: number
  foreigners: number
}

/** Доли групп органов в общем числе протоколов. Сумма — единица. */
const ORG_GROUP_SHARES: Array<{ name: string; share: number }> = [
  { name: 'Органы внутренних дел', share: 0.64 },
  { name: 'Органы государственных доходов', share: 0.12 },
  { name: 'Транспортный контроль', share: 0.08 },
  { name: 'Санитарно-эпидемиологический контроль', share: 0.06 },
  { name: 'Экологический надзор', share: 0.05 },
  { name: 'Прочие органы', share: 0.05 },
]

/** Доли типов субъектов внутри группы. */
const SUBJECT_SHARES = {
  individuals: 0.83,
  legalEntities: 0.09,
  entrepreneurs: 0.045,
  minors: 0.012,
  officials: 0.016,
  foreigners: 0.007,
}

export function buildOrgGroupRows(totalProtocols: number): OrgGroupRow[] {
  return ORG_GROUP_SHARES.map(({ name, share }) => {
    const total = Math.round(totalProtocols * share)
    return {
      name,
      total,
      individuals: Math.round(total * SUBJECT_SHARES.individuals),
      legalEntities: Math.round(total * SUBJECT_SHARES.legalEntities),
      entrepreneurs: Math.round(total * SUBJECT_SHARES.entrepreneurs),
      minors: Math.round(total * SUBJECT_SHARES.minors),
      officials: Math.round(total * SUBJECT_SHARES.officials),
      foreigners: Math.round(total * SUBJECT_SHARES.foreigners),
    }
  })
}

/** Итоги по типам субъектов — для четырёх плиток над таблицей. */
export function buildSubjectTotals(totalProtocols: number) {
  return {
    entrepreneurs: Math.round(totalProtocols * SUBJECT_SHARES.entrepreneurs),
    minors: Math.round(totalProtocols * SUBJECT_SHARES.minors),
    officials: Math.round(totalProtocols * SUBJECT_SHARES.officials),
    foreigners: Math.round(totalProtocols * SUBJECT_SHARES.foreigners),
  }
}

export type PenaltyRow = { label: string; value: number }

/**
 * Взыскания: основные и дополнительные.
 *
 * СУММА ОСНОВНЫХ МЕНЬШЕ ЧИСЛА ПРОТОКОЛОВ НАМЕРЕННО — по части дел взыскание не
 * налагается (прекращение, истечение срока). Равенство выглядело бы аккуратнее
 * и было бы неправдой о том, как работает производство.
 */
export function buildMainPenalties(totalProtocols: number): PenaltyRow[] {
  return [
    { label: 'Предупреждение', value: Math.round(totalProtocols * 0.21) },
    { label: 'Административный штраф', value: Math.round(totalProtocols * 0.63) },
    { label: 'Административный арест', value: Math.round(totalProtocols * 0.018) },
    { label: 'Лишение специального права', value: Math.round(totalProtocols * 0.012) },
  ]
}

export function buildExtraPenalties(totalProtocols: number): PenaltyRow[] {
  return [
    { label: 'Конфискация', value: Math.round(totalProtocols * 0.009) },
    { label: 'Выдворение за пределы РК', value: Math.round(totalProtocols * 0.0012) },
    {
      label: 'Приостановление деятельности',
      value: Math.round(totalProtocols * 0.0035),
    },
  ]
}
